import type { ReactNode } from 'react'
import { Check, Circle, ExternalLink } from 'lucide-react'
import { cn } from '../utils'

export type ChecklistItemData = {
  id: string
  label: string
  completed: boolean
  href?: string
  to?: string
  external?: boolean
}

function ChecklistItemRow({
  item,
  renderLink,
}: {
  item: ChecklistItemData
  renderLink?: (item: ChecklistItemData, children: ReactNode) => ReactNode
}) {
  const content = (
    <div
      className={cn(
        'flex items-center gap-3 py-2 px-2 rounded transition-colors',
        item.completed
          ? 'text-muted-foreground'
          : 'text-foreground hover:bg-accent/30'
      )}
    >
      {item.completed ? (
        <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
      ) : (
        <Circle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      )}
      <span
        className={cn(
          'text-sm flex-1',
          item.completed && 'line-through opacity-60'
        )}
      >
        {item.label}
      </span>
      {(item.href || item.to) && !item.completed && item.external && (
        <ExternalLink className="h-3 w-3 text-muted-foreground" />
      )}
    </div>
  )

  if (!item.completed && (item.href || item.to) && renderLink) {
    return renderLink(item, content)
  }

  return content
}

export type OnboardingChecklistProps = {
  items: ChecklistItemData[]
  title: string
  subtitle: string
  loading?: boolean
  loadingSlot?: ReactNode
  renderLink?: (item: ChecklistItemData, children: ReactNode) => ReactNode
  className?: string
}

export function OnboardingChecklist({
  items,
  title,
  subtitle,
  loading,
  loadingSlot,
  renderLink,
  className,
}: OnboardingChecklistProps) {
  const completedCount = items.filter((i) => i.completed).length

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card p-4',
        className
      )}
    >
      <h3 className="text-sm font-medium text-foreground mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-4">{subtitle}</p>
      <div className="space-y-0.5 border-t border-border/10 pt-3">
        {loading ? (
          loadingSlot ?? (
            <div className="space-y-2 py-2">
              <div className="h-5 w-full bg-muted/30 rounded animate-pulse" />
            </div>
          )
        ) : (
          items.map((item) => (
            <ChecklistItemRow
              key={item.id}
              item={item}
              renderLink={renderLink}
            />
          ))
        )}
      </div>
      <div className="mt-4 pt-3 border-t border-border/10">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {loading ? (
            loadingSlot ?? (
              <>
                <div className="h-3 w-20 bg-muted/30 rounded animate-pulse" />
                <div className="h-1 w-20 bg-muted/30 rounded animate-pulse" />
              </>
            )
          ) : (
            <>
              <span>
                {completedCount}/{items.length} completed
              </span>
              <div className="w-20 h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{
                    width: `${(completedCount / items.length) * 100}%`,
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
